<template>
  <div class="highlights-select-modal">
    <!-- Header -->
    <div class="header highlights-select-modal__header">
      <div class="header__back-link-wrap">
        <LinkWithIcon class="header__back-link" @click="emit('cancel')" isDottedUnderline>
          <template #icon><IconEmbedded name="arrow-left_2-5" :size="16" /></template>
          <span>Back</span>
        </LinkWithIcon>
      </div>

      <h1 class="global-h1 header__title">{{ props.title }}</h1>
      <div v-if="props.subTitle" class="header__subtitle">
        {{ props.subTitle }}
      </div>
    </div>
    <!-- / Header -->

    <!-- Limit warning -->
    <div v-if="isAtLimit" class="limit-warning highlights-select-modal__limit-warning">Limit reached. Social Platform allows posting up to 10 images.</div>
    <!-- / Limit warning -->

    <!-- AI suggested alternatives -->
    <div v-if="props.fallbackAssetIds?.length" class="ai-suggested-alternatives highlights-select-modal__ai-suggested-alternatives">
      <!-- Section header -->
      <div class="section-header ai-suggested-alternatives__section-header">
        <IconEmbedded class="section-header__icon" name="ai-triple-solid" :size="26" color="rgba(17, 134, 137, 1)" />

        <h4 class="global-h4 section-header__title">
          AI Suggested Alternatives
          <!-- <span class="section-header__gray-text">{{ props.fallbackAssetIds?.length }}</span> -->
        </h4>
      </div>
      <!-- / Section header -->

      <ul class="ai-suggested-alternatives__thumbnails-list">
        <SrpFileThumbnail
          class="highlights-select-modal__thumbnail"
          v-for="assetId in props.fallbackAssetIds"
          :key="assetId"
          :fileName="assetId"
          galleryPostfix="highlightsSelectModal"
          :isDeleteButton="false"
          @delete="() => {}"
          isEntireAreaCheckable
        >
          <input
            type="checkbox"
            :value="assetId"
            :checked="_selectedAssetIds.includes(assetId)"
            :disabled="isAtLimit && !_selectedAssetIds.includes(assetId)"
            @change="$event => selectOrDeselectFile(($event.target as HTMLInputElement).checked, assetId)"
          />
        </SrpFileThumbnail>
      </ul>
    </div>
    <!-- / AI suggested alternatives -->

    <ul class="highlights-select-modal__thumbnails-list">
      <SrpFileThumbnail
        class="highlights-select-modal__thumbnail"
        v-for="assetId in props.allAssetIds"
        :key="assetId"
        :fileName="assetId"
        galleryPostfix="highlightsSelectModal"
        :isDeleteButton="false"
        @delete="() => {}"
        isEntireAreaCheckable
      >
        <input
          type="checkbox"
          :value="assetId"
          :checked="_selectedAssetIds.includes(assetId)"
          :disabled="isAtLimit && !_selectedAssetIds.includes(assetId)"
          @change="$event => selectOrDeselectFile(($event.target as HTMLInputElement).checked, assetId)"
        />
      </SrpFileThumbnail>
    </ul>

    <SelectedFilesPanel
      v-if="_selectedAssetIds.length"
      class="highlights-select-modal__selected-files-panel"
      :selectedFiles="_selectedAssetIds.map(id => ({ serverId: id }))"
      @remove="assetId => selectOrDeselectFile(false, assetId)"
      @reorder="newArray => (_selectedAssetIds = newArray.map(({ serverId }) => serverId))"
      :isPreviewLinkVisible="false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue";

// Components
import SrpFileThumbnail from "@components/ui/SrpFileThumbnail.vue";
import SelectedFilesPanel from "@components/SelectedFilesPanel.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";

// Constants
import { MAX_PHOTOS_PER_POST } from "@/constants/social";

const props = withDefaults(
  defineProps<{
    title?: string;
    subTitle?: string;
    allAssetIds: Array<string>;
    fallbackAssetIds: Array<string>;
    selectedAssetIds: Array<string>;
    saveCallBack?: ((selectedAssetIds: Array<string>) => Promise<void>) | null;
  }>(),
  {
    title: "Select Photos",
    subTitle: "",
    allAssetIds: () => [],
    fallbackAssetIds: () => [],
    selectedAssetIds: () => [],
    saveCallBack: null,
  }
);

const emit = defineEmits<{
  (e: "save", selectedAssetIds: Array<string>): void;
  (e: "cancel"): void;
}>();

// Select file ================================================================
function selectOrDeselectFile(isSelected: boolean, assetId: string) {
  if (isSelected) {
    _selectedAssetIds.value.push(assetId);
  } else {
    _selectedAssetIds.value = _selectedAssetIds.value.filter(_assetId => _assetId !== assetId);
  }
}

// selectedAssetIds local copy ================================================
const _selectedAssetIds = ref<Array<string>>([]);

onMounted(() => {
  _selectedAssetIds.value = props.selectedAssetIds;
});

// Selection limit ============================================================
const isAtLimit = computed(() => _selectedAssetIds.value.length >= MAX_PHOTOS_PER_POST);

// Save =======================================================================
const isSaving = ref<boolean>(false);

async function save() {
  if (props.saveCallBack) {
    isSaving.value = true;
    await props.saveCallBack(_selectedAssetIds.value);
    isSaving.value = false;
  }
  emit("save", _selectedAssetIds.value);
}

// Define expose ==============================================================
defineExpose({
  save,
  cancel: () => emit("cancel"),
});
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";
@import "@/scss/mixins/animations/loading-bg.scss";

// Header =====================================================================
.header {
  &__back-link-wrap {
    padding-bottom: 15px;
    margin-bottom: 20px;
    border-bottom: 1px rgba(0, 0, 0, 0.15) solid;
  }

  &__back-link {
  }

  &__title {
    margin-bottom: 10px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__subtitle {
  }
}

// Limit warning ==============================================================
.limit-warning {
  padding: 12px 16px;
  margin-bottom: 20px;
  background: rgba(255, 193, 7, 0.15);
  border: 1px solid rgba(255, 193, 7, 0.6);
  border-radius: 6px;
  color: #856404;
  font-weight: 600;
  text-align: center;
}

// Section header =============================================================
.section-header {
  display: flex;
  align-items: center;

  &__icon {
    margin-right: 2px;
  }

  &__title {
  }

  &__gray-text {
    color: rgba(0, 0, 0, 0.5);
    font-weight: 500;
  }
}

// AI suggested alternatives ==================================================
.ai-suggested-alternatives {
  padding: 23px 0 18px;
  position: relative;
  z-index: 0;

  &::before {
    content: "";
    width: calc(100% + 60px);
    height: 100%;
    position: absolute;
    inset: 0 auto auto -30px;
    z-index: -1;
    background: rgba(229, 243, 242, 1);
  }

  &__section-header {
    margin-bottom: 13px;
  }

  &__thumbnails-list {
    width: calc(100% + 15px);
    padding: 0;
    margin: 0 0 40px;
    display: flex;
    flex-wrap: wrap;
    list-style: none;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

// Highlights select modal ====================================================
.highlights-select-modal {
  color: #5b5b5b;
  font: 14px/19px sans-serif;

  &__header {
    margin-bottom: 33px;
  }

  &__ai-suggested-alternatives {
    margin-bottom: 30px;
  }

  &__thumbnails-list {
    width: calc(100% + 15px);
    padding: 0;
    margin: 0 0 40px;
    display: flex;
    flex-wrap: wrap;
    list-style: none;

    &--skeleton {
      height: calc(100dvh - 300px);
      align-content: flex-start;
      position: relative;
      overflow: hidden;
      mask-image: linear-gradient(180deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 100%);
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__thumbnail {
    width: 113px;
    margin: 0 11px 11px 0;

    &--skeleton {
      @include loadingBgAnimation;
      height: 85px;
    }
  }

  &__selected-files-panel {
    position: sticky;
    bottom: -40px;
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .highlights-select-modal {
    &__thumbnail {
      width: calc(100% / 3 - 12px);
    }

    &__selected-files-panel {
      width: calc(100% + 40px);
      margin-left: -20px;
      bottom: -37px;

      &::after {
        border-radius: 0;
      }
    }
  }
}
</style>
